import { LoadingOverlayService } from '../ui/loading-overlay-service';
import { NativeValidationService } from '../validation/native-validation-service';

export interface DiscountResponse {
    success: boolean;
    message: string;
}

export class FormService {
    private loadingService = LoadingOverlayService.getInstance();
    private validationService = NativeValidationService.getInstance();

    // Helper methods for modern DOM manipulation
    private show(element: HTMLElement): void {
        element.style.display = '';
    }

    private hide(element: HTMLElement): void {
        element.style.display = 'none';
    }

    private setFieldValue(element: HTMLInputElement | HTMLSelectElement, value: string): void {
        element.value = value;
        element.dispatchEvent(new Event('change', { bubbles: true }));
    }

    private hasClass(element: HTMLElement, className: string): boolean {
        return element.classList.contains(className);
    }

    private addClass(element: HTMLElement, className: string): void {
        element.classList.add(className);
    }

    private removeClass(element: HTMLElement, className: string): void {
        element.classList.remove(className);
    }

    private closest(element: HTMLElement, selector: string): HTMLElement | null {
        return element.closest(selector) as HTMLElement;
    }

    private prop(element: HTMLInputElement | HTMLButtonElement, property: string, value: boolean): void {
        (element as any)[property] = value;
    }

    private data(element: HTMLElement, key: string, value?: any): any {
        if (value !== undefined) {
            element.dataset[key] = value;
            return value;
        }
        return element.dataset[key];
    }

    public initialize(): void {
        this.initializeForms();
        this.initializePageHandlers();
        this.initializeGoBackButton();
    }

    private initializeForms(): void {
        this.handleUsualFormReturn = this.handleUsualFormReturn.bind(this);
        this.setupSearchTermHandlers();
        this.setupAjaxForms();
        this.setupSubmitOnChangeElements();
        this.setupDiscountCodeHandlers();
    }

    private setupSearchTermHandlers(): void {
        const searchTermListeners = document.querySelectorAll('.js-form-listen-searchterm');
        
        // Event delegation for search term announcement forms
        document.addEventListener('submit', (e) => {
            const target = e.target as HTMLElement;
            if (target.matches('.js-form-announce-searchterm')) {
                e.preventDefault();
                const searchTermInput = target.querySelector('#pagination-search-box') as HTMLInputElement;
                if (searchTermInput) {
                    const searchTerm = searchTermInput.value;
                    searchTermListeners.forEach((listener) => {
                        listener.innerHTML = searchTerm;
                    });
                    const url = new URL(window.location.href);
                    url.searchParams.set('q', searchTerm);
                    window.history.pushState({}, '', url);
                }
            }
        });
    }

    private setupAjaxForms(): void {
        const ajaxForms = document.querySelectorAll("form.js-ajax") as NodeListOf<HTMLFormElement>;
        
        ajaxForms.forEach((form) => {
            if (!this.data(form, "hasAjaxSubmitEvent")) {
                form.addEventListener("submit", (e) => {
                    e.preventDefault();

                    const doBlockUI = this.hasClass(form, "js-ajax-blockui");

                    if (doBlockUI) {
                        this.loadingService.blockUI({
                            message: 'Please wait...'
                        });
                    }

                    if (this.hasClass(form, "js-page-reset-form")) {
                        this.resetFormPage(form);
                    }

                    this.performAjax(form,
                        (data: any) => {
                            if (data.result === "redirect") {
                                window.location.href = data.url;
                            } else {
                                const replaceOuter = form.dataset.ajaxReplaceOuter;
                                const replace = form.dataset.ajaxReplace;
                                
                                if (replaceOuter) {
                                    const targetElement = document.querySelector(replaceOuter);
                                    if (targetElement) {
                                        targetElement.outerHTML = data;
                                    }
                                } else if (replace) {
                                    const targetElement = document.querySelector(replace);
                                    if (targetElement) {
                                        targetElement.innerHTML = data;
                                    }
                                } else {
                                    if (form.parentElement) {
                                        form.parentElement.innerHTML = data;
                                    }
                                }

                                this.initialize(); // Re-initialize forms after content replacement
                                this.handleUsualFormReturn(form, doBlockUI);
                            }
                        },
                        (_error: any) => {
                            this.handleUsualFormReturn(form, doBlockUI);
                        });
                });

                this.data(form, "hasAjaxSubmitEvent", "true");

                if (this.hasClass(form, "js-submit-on-load")) {
                    form.dispatchEvent(new Event('submit'));
                }
            }
        });
    }

    private setupSubmitOnChangeElements(): void {
        const submitOnChangeElements = document.querySelectorAll(".js-submit-on-change");

        submitOnChangeElements.forEach((element) => {
            if (!this.data(element as HTMLElement, "hasSubmitOnChangeEvent")) {
                element.addEventListener("change", (e) => {
                    const form = this.closest(e.currentTarget as HTMLElement, "form");
                    if (form) {
                        form.dispatchEvent(new Event('submit'));
                    }
                });

                this.data(element as HTMLElement, "hasSubmitOnChangeEvent", "true");
            }
        });
    }

    private setupDiscountCodeHandlers(): void {
        const shopApplyDiscountUrl = "/umbraco/surface/CheckoutSurface/ApplyDiscount";
        const eventApplyDiscountUrl = "/umbraco/surface/EventBookingSurface/ApplyDiscount?id=";

        // Event delegation for discount application buttons
        document.addEventListener("click", (e) => {
            const target = e.target as HTMLElement;
            if (target.matches("a.js-apply-discount, button.js-apply-discount")) {
                this.applyDiscount(target, shopApplyDiscountUrl);
                e.preventDefault();
            }
        });

        // Event delegation for discount input keydown
        document.addEventListener("keydown", (e) => {
            const target = e.target as HTMLElement;
            if (target.matches("input.js-apply-discount") && (e as KeyboardEvent).keyCode === 13) {
                this.applyDiscount(target, shopApplyDiscountUrl);
                e.preventDefault();
                return false;
            }
        });

        // Event delegation for event discount application buttons
        document.addEventListener("click", (e) => {
            const target = e.target as HTMLElement;
            if (target.matches("a.js-apply-discount-event, button.js-apply-discount-event")) {
                const eventIdInput = document.querySelector("input[name=EventId]") as HTMLInputElement;
                const invoiceIdInput = document.querySelector("input[name=InvoiceId]") as HTMLInputElement;
                const eventId = eventIdInput?.value || "";
                const invoiceId = invoiceIdInput?.value || "";
                this.applyDiscount(target, eventApplyDiscountUrl + eventId + invoiceId);
                e.preventDefault();
            }
        });

        // Event delegation for event discount input keydown
        document.addEventListener("keydown", (e) => {
            const target = e.target as HTMLElement;
            if (target.matches("input.js-apply-discount-event") && (e as KeyboardEvent).keyCode === 13) {
                const eventIdInput = document.querySelector("input[name=EventId]") as HTMLInputElement;
                const invoiceIdInput = document.querySelector("input[name=InvoiceId]") as HTMLInputElement;
                const eventId = eventIdInput?.value || "";
                const invoiceId = invoiceIdInput?.value || "";
                this.applyDiscount(target, eventApplyDiscountUrl + eventId + invoiceId);
                e.preventDefault();
                return false;
            }
        });
    }

    private applyDiscount(triggerEl: HTMLElement, url: string): void {
        const container = this.closest(triggerEl, ".discount-code-container");
        if (!container) return;
        
        const messageEl = container.parentElement?.querySelector(".discount-code-message") as HTMLElement;
        const codeInput = container.querySelector("input[name=DiscountCode]") as HTMLInputElement;

        if (!codeInput || !messageEl) return;

        this.prop(triggerEl as HTMLButtonElement, "disabled", true);

        this.addClass(messageEl, "d-none");
        messageEl.textContent = "";

        const formData = new FormData();
        formData.append('code', codeInput.value);

        fetch(url, {
            method: "POST",
            body: formData,
            cache: 'no-cache'
        })
        .then(response => response.json())
        .then((data: DiscountResponse) => {
            if (data.success) {
                location.reload();
                data.message += " Refreshing...";
            } else {
                this.prop(triggerEl as HTMLButtonElement, "disabled", false);
            }

            this.removeClass(messageEl, "d-none");
            messageEl.textContent = data.message;
        })
        .catch((_error) => {
            this.prop(triggerEl as HTMLButtonElement, "disabled", false);
            this.removeClass(messageEl, "d-none");
            messageEl.textContent = "Something went wrong, please try again.";
        });
    }

    private initializePageHandlers(): void {
        // Event delegation for page number triggers
        document.addEventListener("click", (e) => {
            const target = e.target as HTMLElement;
            if (target.matches(".js-page-number-trigger")) {
                e.preventDefault();
                this.changePage(target);
            }
        });

        // Event delegation for page reset forms
        document.addEventListener("submit", (e) => {
            const target = e.target as HTMLElement;
            if (target.matches("form.js-page-reset-form")) {
                const eventData = (e as any).data;
                if (eventData && typeof eventData === "object" && eventData.pageload) {
                    return;
                }
                this.resetFormPage(target as HTMLFormElement);
            }
        });

        // Event delegation for form button locking
        document.addEventListener("submit", (e) => {
            const target = e.target as HTMLElement;
            if (target.matches("form.js-lock-buttons-on-submit")) {
                const buttons = target.querySelectorAll("a.btn, button, input[type=submit]") as NodeListOf<HTMLButtonElement | HTMLInputElement>;
                buttons.forEach(button => this.prop(button, "disabled", true));
            }
        });

        // Event delegation for trigger other elements
        document.addEventListener("click", (e) => {
            const target = e.target as HTMLElement;
            if (target.matches(".js-trigger-other")) {
                e.preventDefault();
                const fieldId = target.dataset.fieldid;
                if (fieldId) {
                    const targetElement = document.getElementById(fieldId) as HTMLElement;
                    if (targetElement) {
                        targetElement.click();
                    }
                }
            }
        });

        // Event delegation for go back triggers
        document.addEventListener("click", (e) => {
            const target = e.target as HTMLElement;
            if (target.matches(".js-goback-trigger")) {
                const form = this.closest(target, "form") as HTMLFormElement;
                if (!form) return;

                const fieldName = target.dataset.field || "GoBack";
                const hiddenField = form.querySelector(`input[name='${fieldName}']`) as HTMLInputElement;
                if (hiddenField) {
                    hiddenField.value = "true";
                }

                const validator = (form as any).validator;
                if (validator && validator.settings) {
                    validator.settings.ignore = "*";
                }

                form.submit();
            }
        });

        // Event delegation for file input changes
        document.addEventListener("change", (e) => {
            const target = e.target as HTMLInputElement;
            if (target.matches(".form__file input[type=file]")) {
                const files = target.files;
                const labelElement = target.parentElement?.querySelector(".form__file--label") as HTMLElement;

                if (files && files.length > 0 && labelElement) {
                    let text = "";
                    for (let i = 0; i < files.length; i++) {
                        text += files[i].name + (i !== files.length - 1 ? ", " : "");
                    }
                    labelElement.textContent = text;
                } else if (labelElement) {
                    labelElement.textContent = labelElement.dataset.default || "";
                }
            }
        });

        // Search icon click handler
        const searchIcon = document.getElementById('search-icon');
        if (searchIcon) {
            searchIcon.addEventListener("click", () => {
                const searchTerm = document.getElementById('SearchTerm') as HTMLInputElement;
                if (searchTerm) {
                    searchTerm.focus();
                }
            });
        }

        // Event delegation for copy value elements
        document.addEventListener("change", (e) => {
            const target = e.target as HTMLInputElement;
            if (target.matches(".js-copy-value")) {
                const form = this.closest(target, "form");
                if (!form) return;

                const fieldName = target.dataset.copytofield;
                if (fieldName) {
                    const targetField = form.querySelector(`input[name='${fieldName}']`) as HTMLInputElement;
                    if (targetField) {
                        this.setFieldValue(targetField, target.value);
                    }
                }
            }
        });

        this.setupPaymentTypeHandlers();
        this.setupBillingAddressHandlers();
    }

    private setupPaymentTypeHandlers(): void {
        const payTypeElements = document.querySelectorAll(".js-pay-type");
        
        payTypeElements.forEach(element => {
            element.addEventListener("click", (e) => {
                e.preventDefault();

                const target = e.currentTarget as HTMLElement;
                const id = target.dataset.id;

                if (id === "card") {
                    document.querySelectorAll('.js-card').forEach(el => this.show(el as HTMLElement));
                    document.querySelectorAll('.js-invoice').forEach(el => this.hide(el as HTMLElement));
                    document.querySelectorAll('.js-directDebit').forEach(el => this.hide(el as HTMLElement));
                    document.querySelectorAll('.js-pay-type').forEach(el => this.removeClass(el as HTMLElement, 'active'));
                    this.addClass(target, 'active');
                    
                    this.setRequiredField("#tAndc", false);
                    this.setRequiredField('#cardholder-name', true);
                    this.setRequiredField('#cardNumber', true);
                    this.setRequiredField('#cardExpiry', true);
                    this.setRequiredField('#cardCVC', true);
                    this.setRequiredField('#flexCheckDefault', true);
                    this.setRequiredField('#DirectDebitSelectFrequency', false);
                    
                    this.setFieldValue(document.querySelector('#PayByInvoice') as HTMLInputElement, 'false');
                    this.setFieldValue(document.querySelector('#PayByCard') as HTMLInputElement, 'true');
                    this.setFieldValue(document.querySelector('#PayByDirectDebit') as HTMLInputElement, 'false');
                }
                else if (id === "invoice") {
                    document.querySelectorAll('.js-card').forEach(el => this.hide(el as HTMLElement));
                    document.querySelectorAll('.js-invoice').forEach(el => this.show(el as HTMLElement));
                    document.querySelectorAll('.js-directDebit').forEach(el => this.hide(el as HTMLElement));
                    document.querySelectorAll('.js-pay-type').forEach(el => this.removeClass(el as HTMLElement, 'active'));
                    this.addClass(target, 'active');
                    
                    this.setRequiredField("#tAndc", true);
                    this.setRequiredField('#cardholder-name', false);
                    this.setRequiredField('#cardNumber', false);
                    this.setRequiredField('#cardExpiry', false);
                    this.setRequiredField('#cardCVC', false);
                    this.setRequiredField('#flexCheckDefault', false);
                    this.setRequiredField('#DirectDebitSelectFrequency', false);
                    
                    this.setFieldValue(document.querySelector('#PayByInvoice') as HTMLInputElement, 'true');
                    this.setFieldValue(document.querySelector('#PayByCard') as HTMLInputElement, 'false');
                    this.setFieldValue(document.querySelector('#PayByDirectDebit') as HTMLInputElement, 'false');
                }
                else if (id === "directDebit") {
                    document.querySelectorAll('.js-card').forEach(el => this.hide(el as HTMLElement));
                    document.querySelectorAll('.js-invoice').forEach(el => this.hide(el as HTMLElement));
                    document.querySelectorAll('.js-directDebit').forEach(el => this.show(el as HTMLElement));
                    document.querySelectorAll('.js-pay-type').forEach(el => this.removeClass(el as HTMLElement, 'active'));
                    this.addClass(target, 'active');
                    
                    this.setRequiredField("#tAndc", true);
                    this.setRequiredField('#cardholder-name', false);
                    this.setRequiredField('#cardNumber', false);
                    this.setRequiredField('#cardExpiry', false);
                    this.setRequiredField('#cardCVC', false);
                    this.setRequiredField('#flexCheckDefault', false);
                    this.setRequiredField('#DirectDebitSelectFrequency', true);
                    
                    this.setFieldValue(document.querySelector('#PayByInvoice') as HTMLInputElement, 'false');
                    this.setFieldValue(document.querySelector('#PayByCard') as HTMLInputElement, 'false');
                    this.setFieldValue(document.querySelector('#PayByDirectDebit') as HTMLInputElement, 'true');
                }
            });
        });
    }

    private setRequiredField(selector: string, required: boolean): void {
        const field = document.querySelector(selector) as HTMLInputElement;
        if (field) {
            if (required) {
                field.setAttribute('required', 'required');
            } else {
                field.removeAttribute('required');
            }
        }
    }

    private setupBillingAddressHandlers(): void {
        const sameAsAboveInputs = document.querySelectorAll('input[name="SameAsAbove"]');

        sameAsAboveInputs.forEach(input => {
            input.addEventListener('change', (e) => {
                this.applyBillingVisibility(e.currentTarget as HTMLInputElement);
            });

            // Set initial state on page load
            if ((input as HTMLInputElement).type === 'checkbox') {
                this.applyBillingVisibility(input as HTMLInputElement);
            }
        });
    }

    private applyBillingVisibility(target: HTMLInputElement): void {
        const hiddenField = document.querySelector('input[type="checkbox"][name="SameAsAbove"]') as HTMLInputElement;
        const checkoutForm = this.closest(target, 'form');

        if (!checkoutForm || !hiddenField) return;

        const billingForm = checkoutForm.querySelector("#billingDetails") as HTMLElement;
        const billingFields = billingForm?.querySelectorAll("input, select") as NodeListOf<HTMLInputElement | HTMLSelectElement>;
        const requiredBillingFields = Array.from(billingFields || []).filter(field =>
            field.hasAttribute("data-val-required")
        );

        if (target.checked) {
            hiddenField.value = "true";
            if (billingForm) this.hide(billingForm);

            requiredBillingFields.forEach(field => {
                delete (field as any).dataset.val;
            });
        } else {
            hiddenField.value = "false";
            if (billingForm) this.show(billingForm);

            requiredBillingFields.forEach(field => {
                (field as any).dataset.val = "true";
            });
        }

        // Remove existing validation state and re-parse.
        // jQuery Validate stores its validator instance in jQuery's internal data cache
        // (not as HTML attributes), so we must use $form.removeData() to fully clear it.
        // Using `delete form.dataset.validator` only removes the HTML attribute and leaves
        // the cached validator intact, causing unobtrusive.parse() to skip re-registration
        // of rules for fields whose data-val was toggled.
        const $ = (window as any).$;
        if ($ && typeof $.fn?.removeData === "function") {
            $(checkoutForm).removeData("validator").removeData("unobtrusiveValidation");
        }

        // Re-initialize validation with native service
        this.validationService.parse(checkoutForm as HTMLFormElement);

        // Re-parse jQuery Unobtrusive Validation so all currently-enabled fields
        // (with data-val="true") get fresh rules registered.
        if ($ && $?.validator?.unobtrusive?.parse) {
            $.validator.unobtrusive.parse(checkoutForm);
        }
    }

    private initializeGoBackButton(): void {
        let formGoBackBtn = document.querySelector('.js-go-back');
        if (formGoBackBtn) {
            formGoBackBtn.addEventListener('click', (e) => {
                e.preventDefault();

                var form = formGoBackBtn.closest('form');
                var goBackHiddenField = form?.querySelector('input[id="GoBack"]') as HTMLInputElement;

                if (form && goBackHiddenField) {
                    goBackHiddenField.value = "True";
                    goBackHiddenField.dispatchEvent(new Event('change'));
                    (form as HTMLFormElement).submit();
                }
            });
        }
    }

    private handleUsualFormReturn(form: HTMLFormElement, doBlockUI: boolean): void {
        if (doBlockUI) {
            this.loadingService.unblockUI();
        }

        const importantContainer = form.querySelector(".error-container, .success-container") as HTMLElement;

        if (importantContainer) {
            if (window.App && window.App.smoothScrollTo) {
                window.App.smoothScrollTo(importantContainer);
            }
        } else {
            // Only scroll to the form automatically if this isn't an onload submission
            const isFirstLoadSubmission = this.hasClass(form, "js-submit-on-load") && !this.data(form, "hasSubmittedBefore");

            if (!isFirstLoadSubmission) {
                if (window.App && window.App.smoothScrollTo) {
                    window.App.smoothScrollTo(form, false, 250);
                }
            }
        }

        this.data(form, "hasSubmittedBefore", "true");
    }

    private async performAjax(form: HTMLFormElement, successFunc: Function, errorFunc: Function): Promise<void> {
        try {
            const method = form.getAttribute("method") || "POST";
            const url = form.getAttribute("action") || "";
            
            let body: FormData | URLSearchParams;
            let headers: HeadersInit = {};

            if (this.hasClass(form, "js-formdata")) {
                body = new FormData(form);
            } else {
                body = new URLSearchParams(new FormData(form) as any);
                headers['Content-Type'] = 'application/x-www-form-urlencoded';
            }

            const response = await fetch(url, {
                method: method,
                body: body,
                headers: headers,
                cache: 'no-cache'
            });

            if (!response.ok) {
                throw new Error(`HTTP error! status: ${response.status}`);
            }

            const contentType = response.headers.get('content-type');
            let data;
            
            if (contentType && contentType.includes('application/json')) {
                data = await response.json();
            } else {
                data = await response.text();
            }

            successFunc(data);
        } catch (error) {
            errorFunc(error);
        }
    }

    private resetFormPage(form: HTMLFormElement): void {
        const activeElement = document.activeElement as HTMLElement;

        // If there is an active element, and it is within this form, and it isn't a pagination button, then reset the page
        if (activeElement && form.contains(activeElement) && 
            !this.hasClass(activeElement, "js-page-number-trigger") && 
            !this.hasClass(activeElement, "page-link")) {
            
            const pageVar = form.dataset.targetField;
            const pageName = pageVar || "Pagination.CurrentPage";

            const pageField = form.querySelector(`[name='${pageName}']`) as HTMLInputElement;
            if (pageField) {
                this.setFieldValue(pageField, "1");
            }
        }
    }

    private changePage(element: HTMLElement, pageNum?: number): void {
        const page = pageNum !== undefined ? pageNum : element.dataset.targetPage;

        if (page !== "" && page !== undefined) {
            const isSafari = navigator.vendor && navigator.vendor.indexOf("Apple") > -1 &&
                navigator.userAgent &&
                navigator.userAgent.indexOf("CriOS") === -1 &&
                navigator.userAgent.indexOf("FxiOS") === -1;

            const pageVar = element.dataset.targetField;
            const form = this.closest(element, "form") as HTMLFormElement;
            if (!form) return;
            
            const pageName = pageVar || "Pagination.CurrentPage";

            const varElement = form.querySelector(`[name='${pageName}']`) as HTMLInputElement;
            if (varElement) {
                this.setFieldValue(varElement, String(page));

                if (isSafari) {
                    // Actually disgusting that I have to do this for Safari
                    const replaceVarElement = document.createElement("input");
                    replaceVarElement.id = varElement.id + "S";
                    replaceVarElement.name = (pageVar && pageVar.replace(/"/g, "") || varElement.name) + "S";
                    replaceVarElement.type = "hidden";
                    replaceVarElement.value = String(page);

                    varElement.parentElement?.appendChild(replaceVarElement);
                }

                form.submit();
            }
        }
    }
}

// Global window extension for compatibility
declare global {
    interface Window {
        App?: any;
    }
}
